import type { WorkflowRuleAction } from '@/pages/ats-job-creation/tabs/interview-plan/stage/automation/api/workflowRule.types';
import { MOVE_WITH_AUTOMATION_STRINGS } from './moveWithAutomation.strings';

const STR = MOVE_WITH_AUTOMATION_STRINGS;

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * 60;
const SECONDS_PER_DAY = 60 * 60 * 24;

// `delay_seconds` really is SECONDS (BE schedules `Time.current + delay_seconds`).
// Largest natural unit; odd legacy values fall back to whole minutes.
const secondsToDurationLabel = (seconds: number): string => {
  if (seconds <= 0) return '';
  if (seconds % SECONDS_PER_DAY === 0) {
    return STR.duration.days(seconds / SECONDS_PER_DAY);
  }
  if (seconds % SECONDS_PER_HOUR === 0) {
    return STR.duration.hours(seconds / SECONDS_PER_HOUR);
  }
  // Clamp so a sub-minute delay never reads as "0 minutes".
  return STR.duration.minutes(
    Math.max(1, Math.round(seconds / SECONDS_PER_MINUTE))
  );
};

type ActionConfig = {
  ats_job_stage_action_id?: number;
  // BE trigger vocabulary: `sent`, plus the submission family
  // (`candidate_submitted` / `scorecard_submitted`). `submitted` is the
  // legacy single value, kept so older rules still format correctly.
  trigger_on?:
    | 'sent'
    | 'scheduled'
    | 'completed'
    | 'submitted'
    | 'candidate_submitted'
    | 'scorecard_submitted';
};

// The candidate finishing the previous activity (`candidate_submitted`, and
// the legacy `submitted`) and an interview being `completed` all read as the
// activity being *completed*. `scorecard_submitted` is phrased separately
// (reviewers submitting scorecards).
const COMPLETED_TRIGGERS = new Set([
  'submitted',
  'candidate_submitted',
  'completed',
]);

const labelFor = (action: WorkflowRuleAction): string =>
  action.target?.label ?? action.target?.kind ?? STR.fallback.previousActivity;

// Returns a human-readable timing string for a single workflow rule
// action, given the full ordered actions list (so we can look up the
// previous action's label for chained triggers).
//
// Position 1 actions trigger off "stage entry". Position > 1 actions
// trigger relative to the action immediately before them in the rule.
export const formatAutomationTiming = (
  action: WorkflowRuleAction,
  actions: WorkflowRuleAction[]
): string => {
  const delaySeconds = action.delay_seconds ?? 0;
  const config = (action.action_config ?? {}) as ActionConfig;
  const isFirst = action.position === 1;

  const previous = actions.find((a) => a.position === action.position - 1);
  const previousLabel = previous
    ? labelFor(previous)
    : STR.fallback.previousActivity;

  if (isFirst) {
    if (delaySeconds > 0) {
      return STR.timing.afterStageEntry(secondsToDurationLabel(delaySeconds));
    }
    return STR.timing.immediately;
  }

  // Children may carry a trigger_on that fires relative to the previous
  // activity. When set, this wins over delay — they're alternative trigger
  // semantics, not additive.
  if (config.trigger_on === 'scorecard_submitted') {
    return STR.timing.afterScorecardsSubmitted(previousLabel);
  }
  if (config.trigger_on && COMPLETED_TRIGGERS.has(config.trigger_on)) {
    return STR.timing.afterCompleted(previousLabel);
  }
  if (config.trigger_on === 'scheduled') {
    return STR.timing.afterScheduled(previousLabel);
  }
  if (config.trigger_on === 'sent') {
    return STR.timing.afterSent(previousLabel);
  }

  if (delaySeconds > 0) {
    return STR.timing.afterPrevious(
      secondsToDurationLabel(delaySeconds),
      previousLabel
    );
  }
  return STR.timing.immediatelyAfter(previousLabel);
};
